import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DriverLoadingPage, triggerToastDS } from "@qivia/ui";
import { VehicleRestitutionReturnStep } from "./vehicleRestitutionReturnStep";
import { VehicleRestitutionConditionStep } from "./vehicleRestitutionConditionStep";
import { VehicleRestitutionSummaryStep } from "./vehicleRestitutionSummaryStep";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  selectLatestTelemetryMileage,
  selectVehicleUuid,
} from "../../qrCode/vehicleLoginSlice";
import { selectDriver } from "../../qrCode/driverKnownSlice";
import {
  createInitialVehicleRestitutionAsync,
  selectRestitutionPhotos,
  selectVehicleRestitutionCreated,
  selectCreateInitialVehicleRestitutionStatus,
  selectUpdateInitialVehicleRestitutionStatus,
  updateInitialVehicleRestitutionAsync,
  vehicleSessionsSlice,
} from "./vehicleSessionsSlice";
import { ErrorPage } from "../../components/ErrorPage";

type RestitutionStep = "return" | "condition" | "summary";

export const VehicleRestitutionPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const vehicleUuid = useAppSelector(selectVehicleUuid);
  const latestTelemetryMileage = useAppSelector(selectLatestTelemetryMileage);
  const photos = useAppSelector(selectRestitutionPhotos);
  const driver = useAppSelector(selectDriver);
  const vehicleRestitutionCreated = useAppSelector(
    selectVehicleRestitutionCreated,
  );
  const createInitialVehicleRestitutionStatus = useAppSelector(
    selectCreateInitialVehicleRestitutionStatus,
  );
  const updateInitialVehicleRestitutionStatus = useAppSelector(
    selectUpdateInitialVehicleRestitutionStatus,
  );
  const [step, setStep] = useState<RestitutionStep>("return");
  const [isEditing, setIsEditing] = useState(false);
  const [departureLocation, setDepartureLocation] = useState("");
  const [furthestDestination, setFurthestDestination] = useState("");
  const [mileage, setMileage] = useState("");
  const [isCabinCleaned, setIsCabinCleaned] = useState(false);
  const [isTrunkCleaned, setIsTrunkCleaned] = useState(false);
  const [observations, setObservations] = useState("");

  const hasTelemetryMileage = useMemo(
    () => latestTelemetryMileage !== null && latestTelemetryMileage > 0,
    [latestTelemetryMileage],
  );
  const displayedMileage = useMemo(
    () => (hasTelemetryMileage ? String(latestTelemetryMileage) : mileage),
    [hasTelemetryMileage, latestTelemetryMileage, mileage],
  );
  const photoFiles = useMemo(() => photos.map((photo) => photo.file), [photos]);
  const updatePhotos = useCallback(
    (files: File[]) =>
      dispatch(vehicleSessionsSlice.actions.setRestitutionPhotos(files)),
    [dispatch],
  );

  useEffect(() => {
    if (createInitialVehicleRestitutionStatus === "success") {
      setStep("summary");
      dispatch(
        vehicleSessionsSlice.actions.resetCreateInitialVehicleRestitutionStatus(),
      );
    }
    if (createInitialVehicleRestitutionStatus === "failed") {
      triggerToastDS(t("restitution.summary.error.text") || "", "ERROR");
      dispatch(
        vehicleSessionsSlice.actions.resetCreateInitialVehicleRestitutionStatus(),
      );
    }
  }, [createInitialVehicleRestitutionStatus, t, dispatch]);

  useEffect(() => {
    if (updateInitialVehicleRestitutionStatus === "success") {
      setStep("summary");
      dispatch(
        vehicleSessionsSlice.actions.resetUpdateInitialVehicleRestitutionStatus(),
      );
    }
    if (updateInitialVehicleRestitutionStatus === "failed") {
      triggerToastDS(t("restitution.update.error.text") || "", "ERROR");
      dispatch(
        vehicleSessionsSlice.actions.resetUpdateInitialVehicleRestitutionStatus(),
      );
    }
  }, [updateInitialVehicleRestitutionStatus, t, dispatch]);

  if (!vehicleUuid) return <ErrorPage page="qrCode" />;

  const submitRestitution = async () => {
    const pictures = await Promise.all(
      photos.map(async ({ file }) => {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const fileBuffer = btoa(
          Array.from(bytes, (b) => String.fromCharCode(b)).join(""),
        );
        return { originalFileName: file.name, fileBuffer };
      }),
    );
    const declaration = {
      mileage: Number(displayedMileage),
      isCabinCleaned,
      isTrunkCleaned,
      observations: observations === "" ? null : observations,
      departureLocation,
      furthestDestination:
        furthestDestination === "" ? null : furthestDestination,
    };
    if (isEditing && vehicleRestitutionCreated && driver) {
      void dispatch(
        updateInitialVehicleRestitutionAsync({
          ...declaration,
          restitutionUuid: vehicleRestitutionCreated,
          driverUuid: driver,
          pictures: [],
          picturesToUpload: pictures,
        }),
      );
      return;
    }
    if (isEditing) return;
    void dispatch(
      createInitialVehicleRestitutionAsync({
        ...declaration,
        vehicleUuid,
        pictures,
      }),
    );
  };

  switch (step) {
    case "return":
      return (
        <VehicleRestitutionReturnStep
          departureLocation={departureLocation}
          setDepartureLocation={setDepartureLocation}
          furthestDestination={furthestDestination}
          setFurthestDestination={setFurthestDestination}
          mileage={displayedMileage}
          setMileage={setMileage}
          hasTelemetryMileage={hasTelemetryMileage}
          onReturn={() => navigate(-1)}
          onSubmit={() => setStep("condition")}
        />
      );
    case "condition":
      if (createInitialVehicleRestitutionStatus === "processing") {
        return <DriverLoadingPage />;
      }
      if (updateInitialVehicleRestitutionStatus === "processing") {
        return <DriverLoadingPage />;
      }
      return (
        <VehicleRestitutionConditionStep
          isCabinCleaned={isCabinCleaned}
          setIsCabinCleaned={setIsCabinCleaned}
          isTrunkCleaned={isTrunkCleaned}
          setIsTrunkCleaned={setIsTrunkCleaned}
          observations={observations}
          setObservations={setObservations}
          photos={photoFiles}
          setPhotos={updatePhotos}
          onReturn={() => setStep("return")}
          onValidate={() => void submitRestitution()}
        />
      );
    case "summary":
      return (
        <VehicleRestitutionSummaryStep
          departureLocation={departureLocation}
          furthestDestination={furthestDestination}
          mileage={displayedMileage}
          isCabinCleaned={isCabinCleaned}
          isTrunkCleaned={isTrunkCleaned}
          observations={observations}
          photos={photoFiles}
          isEdited={isEditing}
          onEdit={() => {
            setIsEditing(true);
            setStep("return");
          }}
        />
      );
  }
};
